import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { Send, Sparkles, TrendingUp, Clock, Zap, BarChart3, Globe, Smartphone, MousePointerClick, ArrowUpDown } from 'lucide-react';

export default function NLQInterface({ projectId }) {
  const [question, setQuestion] = useState('');
  const [dateRange, setDateRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [typingText, setTypingText] = useState('');
  const typingRef = useRef(null);

  // Typing animation for the latest AI answer
  useEffect(() => {
    if (results.length > 0 && results[0]._isNew) {
      const fullText = results[0].answer;
      let i = 0;
      setTypingText('');
      typingRef.current = setInterval(() => {
        i++;
        setTypingText(fullText.slice(0, i));
        if (i >= fullText.length) {
          clearInterval(typingRef.current);
          // Mark as fully typed
          setResults(prev => prev.map((r, idx) => idx === 0 ? { ...r, _isNew: false, _typed: true } : r));
        }
      }, 12);
      return () => clearInterval(typingRef.current);
    }
  }, [results.length]);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post('/nlq', {
        project_id: projectId,
        question: question,
        date_range: dateRange
      });

      setResults([{ question, ...response.data, _isNew: true, _typed: false }, ...results]);
      setQuestion('');
      toast.success('Query processed successfully');
    } catch (error) {
      console.error('NLQ Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process question');
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    { text: "What's my traffic trend this week?", icon: TrendingUp, category: "Traffic" },
    { text: "Why did bounce rate increase?", icon: ArrowUpDown, category: "Bounce" },
    { text: "Which pages are most popular?", icon: BarChart3, category: "Pages" },
    { text: "Where is my traffic coming from?", icon: Globe, category: "Geo" },
    { text: "What devices are visitors using?", icon: Smartphone, category: "Devices" },
    { text: "How many conversions this month?", icon: MousePointerClick, category: "Conversion" },
    { text: "What's the average session duration?", icon: Clock, category: "Duration" },
    { text: "What are my top traffic sources?", icon: Zap, category: "Sources" },
  ];

  return (
    <div className="space-y-6" data-testid="nlq-interface">
      {/* Input Section */}
      <Card className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none p-4 sm:p-6 lg:p-8 transition-colors border border-transparent dark:border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="shrink-0 w-12 h-12 bg-[#FEAB3D] dark:bg-[#021426]/20 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-[#021426] dark:text-[#FEAB3D]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Ask Anything About Your Data</h2>
            <p className="text-slate-600 dark:text-slate-400">Get AI-powered insights in natural language</p>
          </div>
        </div>

        <form onSubmit={handleAsk} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="nlq-question-input"
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g., Why did bounce rate increase?"
              className="min-w-0 flex-1 text-base sm:text-lg py-6 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              disabled={loading}
            />
            <select
              data-testid="nlq-date-range-selector"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium focus:outline-none focus:ring-2 focus:ring-[#FEAB3D]"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
            <Button
              data-testid="nlq-submit-button"
              type="submit"
              disabled={loading || !question.trim()}
              className="bg-[#FEAB3D] text-[#021426] px-8 py-6 hover:bg-[#e8960a] hover:text-[#021426] dark:hover:text-[#021426]"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-[#021426] border-t-transparent rounded-full animate-spin" />
                  Thinking...
                </span>
              ) : (
                <><Send className="w-5 h-5 mr-2" />Ask</>
              )}
            </Button>
          </div>
        </form>

        {/* Suggested Questions — enhanced grid */}
        {results.length === 0 && (
          <div className="mt-6">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 font-medium">Try asking:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
              {suggestedQuestions.map((q, index) => {
                const Icon = q.icon;
                return (
                  <button
                    key={index}
                    data-testid={`suggested-question-${index}`}
                    onClick={() => setQuestion(q.text)}
                    className="flex items-center gap-2 px-3 py-2.5 bg-[#FFF6E5] dark:bg-[#021426]/20 text-[#021426] dark:text-[#FEAB3D] rounded-xl text-sm font-medium hover:bg-[#FFE5B2] dark:hover:bg-[#021426]/40 transition-all hover:scale-[1.02] text-left"
                  >
                    <Icon className="w-4 h-4 flex-shrink-0 opacity-70" />
                    <span className="truncate">{q.text}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Results */}
      <div className="space-y-6">
        {results.map((result, index) => (
          <Card key={index} className="bg-white dark:bg-card rounded-2xl shadow-lg dark:shadow-none p-8 slide-in transition-colors border border-transparent dark:border-border" data-testid={`nlq-result-${index}`}>
            <div className="mb-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 bg-[#FEAB3D]/20 dark:bg-[#FEAB3D]/40 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <TrendingUp className="w-4 h-4 text-[#021426] dark:text-[#FEAB3D]" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">Your Question</div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-white">{result.question}</div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-800 pt-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#FFF6E5] dark:bg-[#021426]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Sparkles className="w-4 h-4 text-[#021426] dark:text-[#FEAB3D]" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">AI Answer</div>
                  <div className="text-slate-900 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                    {index === 0 && result._isNew ? (
                      <>
                        {typingText}
                        <span className="inline-block w-0.5 h-5 bg-[#FEAB3D] animate-pulse ml-0.5 align-middle" />
                      </>
                    ) : (
                      result.answer
                    )}
                  </div>

                  {/* Key Data Points */}
                  {result.data && Object.keys(result.data).length > 0 && (index > 0 || result._typed) && (
                    <div className="mt-4 flex flex-wrap gap-3">
                      {Object.entries(result.data).filter(([k]) => k !== 'period_days').map(([key, value]) => (
                        <div key={key} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm">
                          <span className="text-slate-500 dark:text-slate-400 capitalize">{key.replace(/_/g, ' ')}: </span>
                          <span className="font-semibold text-slate-800 dark:text-slate-200">
                            {typeof value === 'number' ? value.toLocaleString() : typeof value === 'object' ? JSON.stringify(value).slice(0, 40) : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {result.insights && result.insights.length > 0 && (index > 0 || result._typed) && (
                    <div className="mt-4 space-y-2">
                      {result.insights.map((insight, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-[#021426] dark:text-[#FEAB3D] bg-[#FFF6E5] dark:bg-[#021426]/20 px-3 py-2 rounded-lg">
                          <Sparkles className="w-4 h-4 text-[#021426] dark:text-[#FEAB3D] flex-shrink-0 mt-0.5" />
                          <span>{insight}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
